// =============================================================================
// BrightPath - Health Domain TypeScript Interfaces
// Package: BP-03-04-P1 (Health Frontend - Foundation)
// =============================================================================

import type { User } from './user';

// =============================================================================
// Health Log Types (Reference Data)
// =============================================================================

export interface FieldSchema {
  name: string;
  type: 'string' | 'number' | 'boolean' | 'array' | 'date' | 'time' | 'select';
  label: string;
  required?: boolean;
  placeholder?: string;
  min?: number;
  max?: number;
  options?: { value: string; label: string }[];
  unit?: string;
  helpText?: string;
}

export interface HealthLogType {
  id: string;
  slug: HealthLogTypeSlug;
  name: string;
  description?: string;
  icon: string;
  color: string;
  fieldsSchema: FieldSchema[];
  displayOrder: number;
  isActive: boolean;
}

export type HealthLogTypeSlug =
  | 'seizure'
  | 'medication'
  | 'symptom'
  | 'sleep'
  | 'feeding'
  | 'therapy'
  | 'vital'
  | 'mood';

// =============================================================================
// Attachments
// =============================================================================

export interface Attachment {
  id: string;
  filename: string;
  originalFilename: string;
  fileType: string;
  fileSizeBytes: number;
  mimeType: string;
  url: string;
  thumbnailUrl?: string;
  createdAt: string;
}

// =============================================================================
// Health Logs
// =============================================================================

export interface HealthLog {
  id: string;
  childId: string;
  logTypeId: string;
  logType: HealthLogType;
  loggedById: string;
  loggedBy: Pick<User, 'id' | 'firstName' | 'lastName' | 'avatarUrl'>;
  occurredAt: string;
  durationMinutes?: number;
  severity?: number;
  data: HealthLogData;
  notes?: string;
  attachments: Attachment[];
  location?: string;
  createdAt: string;
  updatedAt: string;
}

// Type-specific data payloads
export type HealthLogData =
  | SeizureLogData
  | MedicationLogData
  | SymptomLogData
  | SleepLogData
  | FeedingLogData
  | TherapyLogData
  | VitalLogData
  | MoodLogData
  | Record<string, unknown>;

export interface SeizureLogData {
  durationSeconds: number;
  seizureType: SeizureType;
  triggers?: string[];
  preIctalSigns?: string[];
  postIctalState?: string;
  recoveryTimeMinutes?: number;
  rescueMedicationGiven?: boolean;
  rescueMedicationName?: string;
  consciousness?: 'aware' | 'impaired' | 'lost';
  bodyPartsAffected?: string[];
}

export type SeizureType =
  | 'focal_aware'
  | 'focal_impaired'
  | 'focal_to_bilateral'
  | 'generalized_tonic_clonic'
  | 'generalized_absence'
  | 'generalized_myoclonic'
  | 'generalized_atonic'
  | 'unknown'
  | 'other';

export interface MedicationLogData {
  medicationId?: string;
  medicationName: string;
  dosage: string;
  route?: MedicationRoute;
  scheduledTime?: string;
  wasSkipped?: boolean;
  skipReason?: string;
}

export type MedicationRoute =
  | 'oral'
  | 'sublingual'
  | 'rectal'
  | 'injection'
  | 'topical'
  | 'inhaled'
  | 'gtube'
  | 'iv'
  | 'other';

export interface SymptomLogData {
  symptomType: string;
  severity: number;
  bodyLocation?: string;
  duration?: string;
  possibleCauses?: string[];
  interventions?: string[];
  relievedBy?: string;
}

export interface SleepLogData {
  durationHours: number;
  quality: 1 | 2 | 3 | 4 | 5;
  bedTime?: string;
  wakeTime?: string;
  nightWakings?: number;
  sleepLatencyMinutes?: number;
  sleepEnvironment?: string;
  usedSleepAids?: boolean;
  sleepAidDetails?: string;
}

export interface FeedingLogData {
  feedingType: 'oral' | 'gtube' | 'ngtube' | 'bottle' | 'breast' | 'combination';
  amount?: string;
  amountUnit?: 'ml' | 'oz' | 'tbsp' | 'cups';
  durationMinutes?: number;
  foodItems?: string[];
  texture?: 'liquid' | 'puree' | 'soft' | 'regular';
  tolerated?: boolean;
  issues?: string[];
  calorieEstimate?: number;
}

export interface TherapyLogData {
  therapyType: TherapyType;
  providerName?: string;
  providerId?: string;
  sessionDurationMinutes?: number;
  goalsWorkedOn?: string[];
  progressNotes?: string;
  homeExercisesAssigned?: string[];
  nextSessionDate?: string;
  participationLevel?: 1 | 2 | 3 | 4 | 5;
}

export type TherapyType =
  | 'physical_therapy'
  | 'occupational_therapy'
  | 'speech_therapy'
  | 'behavioral_therapy'
  | 'aquatic_therapy'
  | 'hippotherapy'
  | 'music_therapy'
  | 'art_therapy'
  | 'vision_therapy'
  | 'feeding_therapy'
  | 'other';

export interface VitalLogData {
  temperature?: number;
  temperatureUnit?: 'F' | 'C';
  heartRate?: number;
  bloodPressureSystolic?: number;
  bloodPressureDiastolic?: number;
  oxygenSaturation?: number;
  respiratoryRate?: number;
  weight?: number;
  weightUnit?: 'lbs' | 'kg';
  height?: number;
  heightUnit?: 'in' | 'cm';
  bloodGlucose?: number;
}

export interface MoodLogData {
  moodScore: number; // 1-10
  energyLevel?: number; // 1-10
  anxietyLevel?: number; // 1-10
  behaviors?: string[];
  triggers?: string[];
  copingStrategiesUsed?: string[];
  socialInteraction?: 'none' | 'minimal' | 'moderate' | 'good' | 'excellent';
  selfRegulation?: 'poor' | 'fair' | 'good' | 'excellent';
}

// =============================================================================
// Health Log DTOs (Create/Update)
// =============================================================================

export interface CreateHealthLogDTO {
  logTypeId: string;
  occurredAt: string;
  durationMinutes?: number;
  severity?: number;
  data: HealthLogData;
  notes?: string;
  location?: string;
  attachmentIds?: string[];
}

export interface UpdateHealthLogDTO extends Partial<CreateHealthLogDTO> {}

export interface HealthLogParams {
  page?: number;
  pageSize?: number;
  logTypeIds?: string[];
  startDate?: string;
  endDate?: string;
  severityMin?: number;
  severityMax?: number;
  searchQuery?: string;
  sortBy?: 'occurredAt' | 'createdAt' | 'severity';
  sortOrder?: 'asc' | 'desc';
}

// =============================================================================
// Medications
// =============================================================================

export interface Medication {
  id: string;
  childId: string;
  name: string;
  genericName?: string;
  dosage: string;
  frequency: string;
  route?: MedicationRoute;
  prescribingDoctor?: string;
  pharmacy?: string;
  rxNumber?: string;
  startDate?: string;
  endDate?: string;
  instructions?: string;
  sideEffects?: string;
  warnings?: string;
  isActive: boolean;
  reminderEnabled: boolean;
  reminderTimes: string[];
  refillDate?: string;
  pillCount?: number;
  createdAt: string;
  updatedAt: string;
}

export interface MedicationAdministration {
  id: string;
  medicationId: string;
  administeredById: string;
  administeredBy: Pick<User, 'id' | 'firstName' | 'lastName'>;
  scheduledTime: string;
  actualTime: string;
  dosage: string;
  wasSkipped: boolean;
  skipReason?: string;
  notes?: string;
  createdAt: string;
}

export interface CreateMedicationDTO {
  name: string;
  genericName?: string;
  dosage: string;
  frequency: string;
  route?: MedicationRoute;
  prescribingDoctor?: string;
  pharmacy?: string;
  rxNumber?: string;
  startDate?: string;
  endDate?: string;
  instructions?: string;
  sideEffects?: string;
  warnings?: string;
  reminderEnabled?: boolean;
  reminderTimes?: string[];
  refillDate?: string;
  pillCount?: number;
}

export interface UpdateMedicationDTO extends Partial<CreateMedicationDTO> {
  isActive?: boolean;
}

export interface LogMedicationAdministrationDTO {
  scheduledTime: string;
  actualTime: string;
  dosage: string;
  wasSkipped?: boolean;
  skipReason?: string;
  notes?: string;
}

export interface MedicationParams {
  isActive?: boolean;
  searchQuery?: string;
}

export interface MedicationHistoryParams {
  startDate?: string;
  endDate?: string;
  page?: number;
  pageSize?: number;
}

// =============================================================================
// Statistics & Analytics
// =============================================================================

export interface HealthLogStats {
  totalLogs: number;
  logsByType: Record<string, number>;
  averageSeverity?: number;
  logsThisPeriod: number;
  logsPreviousPeriod: number;
  percentChange: number;
  trendsData: TrendDataPoint[];
  periodComparison?: PeriodComparison;
}

export interface TrendDataPoint {
  date: string;
  count: number;
  averageSeverity?: number;
  byType?: Record<string, number>;
}

export interface PeriodComparison {
  currentPeriod: {
    start: string;
    end: string;
    total: number;
  };
  previousPeriod: {
    start: string;
    end: string;
    total: number;
  };
  percentChange: number;
  trend: 'up' | 'down' | 'stable';
}

export interface SeizureStats {
  totalSeizures: number;
  seizureFreeDays: number;
  currentSeizureFreeStreak: number;
  longestSeizureFreeStreak: number;
  averageDurationSeconds: number;
  averagePerDay: number;
  averagePerWeek: number;
  byType: Record<SeizureType, number>;
  byTrigger: Record<string, number>;
  byTimeOfDay: {
    morning: number;
    afternoon: number;
    evening: number;
    night: number;
  };
  dailyData: DailySeizureData[];
  weeklyData: WeeklySeizureData[];
  monthlyData: MonthlySeizureData[];
}

export interface DailySeizureData {
  date: string;
  count: number;
  totalDurationSeconds: number;
  types: Record<string, number>;
}

export interface WeeklySeizureData {
  weekStart: string;
  weekEnd: string;
  count: number;
  averageDuration: number;
}

export interface MonthlySeizureData {
  month: string;
  year: number;
  count: number;
  seizureFreeDays: number;
}

export interface SleepStats {
  averageDurationHours: number;
  averageQuality: number;
  averageBedTime: string;
  averageWakeTime: string;
  averageNightWakings: number;
  totalLogsThisPeriod: number;
  bestNight: {
    date: string;
    durationHours: number;
    quality: number;
  };
  worstNight: {
    date: string;
    durationHours: number;
    quality: number;
  };
  dailyData: DailySleepData[];
  qualityDistribution: Record<number, number>;
}

export interface DailySleepData {
  date: string;
  durationHours: number;
  quality: number;
  bedTime?: string;
  wakeTime?: string;
  nightWakings?: number;
}

export interface MoodStats {
  averageMoodScore: number;
  averageEnergyLevel: number;
  averageAnxietyLevel: number;
  moodDistribution: Record<number, number>;
  commonBehaviors: { behavior: string; count: number }[];
  commonTriggers: { trigger: string; count: number }[];
  dailyData: DailyMoodData[];
  trend: 'improving' | 'declining' | 'stable';
}

export interface DailyMoodData {
  date: string;
  averageMoodScore: number;
  averageEnergyLevel?: number;
  averageAnxietyLevel?: number;
  logCount: number;
}

export interface MedicationAdherenceStats {
  totalScheduled: number;
  totalTaken: number;
  totalSkipped: number;
  adherenceRate: number;
  byMedication: {
    medicationId: string;
    medicationName: string;
    scheduled: number;
    taken: number;
    skipped: number;
    adherenceRate: number;
  }[];
  dailyData: {
    date: string;
    scheduled: number;
    taken: number;
    skipped: number;
  }[];
  skipReasons: { reason: string; count: number }[];
}

export interface StatsParams {
  startDate?: string;
  endDate?: string;
  period?: 'day' | 'week' | 'month' | 'quarter' | 'year';
  compareWithPrevious?: boolean;
}

// =============================================================================
// Export Types
// =============================================================================

export interface ExportParams {
  startDate: string;
  endDate: string;
  format: 'pdf' | 'csv' | 'xlsx';
  includeTypes?: HealthLogTypeSlug[];
  includeMedications?: boolean;
  includeCharts?: boolean;
  includeNotes?: boolean;
}

export interface ExportResult {
  url: string;
  filename: string;
  expiresAt: string;
}

// =============================================================================
// Paginated Response
// =============================================================================

export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
  hasNext: boolean;
  hasPrevious: boolean;
}

// =============================================================================
// Today's Medications View
// =============================================================================

export interface TodaysMedication {
  medication: Medication;
  scheduledTimes: {
    time: string;
    status: 'pending' | 'taken' | 'skipped' | 'late';
    administrationId?: string;
    actualTime?: string;
  }[];
  nextDue?: string;
}

// =============================================================================
// Quick Add Types
// =============================================================================

export interface QuickAddConfig {
  type: HealthLogTypeSlug;
  label: string;
  icon: string;
  color: string;
  defaultData?: Partial<HealthLogData>;
}

export const QUICK_ADD_OPTIONS: QuickAddConfig[] = [
  { type: 'seizure', label: 'Seizure', icon: 'Zap', color: '#E57373' },
  { type: 'medication', label: 'Medication', icon: 'Pill', color: '#C4785A' },
  { type: 'symptom', label: 'Symptom', icon: 'Thermometer', color: '#FFB74D' },
  { type: 'sleep', label: 'Sleep', icon: 'Moon', color: '#6B8DD6' },
  { type: 'feeding', label: 'Feeding', icon: 'UtensilsCrossed', color: '#81C784' },
  { type: 'therapy', label: 'Therapy', icon: 'Activity', color: '#7A9E7E' },
  { type: 'vital', label: 'Vitals', icon: 'Heart', color: '#F06292' },
  { type: 'mood', label: 'Mood', icon: 'Smile', color: '#9575CD' },
];
